import React, { useEffect, useState } from 'react';

const CoffeeRain = () => {
  const [coffees, setCoffees] = useState([]);

  useEffect(() => {
    // Create 30 falling coffee cups
    const newCoffees = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 1,
      rotation: Math.random() * 360
    }));
    setCoffees(newCoffees);
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: 9999,
      overflow: 'hidden'
    }}>
      {coffees.map(coffee => (
        <div
          key={coffee.id}
          style={{
            position: 'absolute',
            top: '-50px',
            left: `${coffee.left}%`,
            fontSize: '2rem',
            animation: `fall ${coffee.duration}s linear ${coffee.delay}s`,
            transform: `rotate(${coffee.rotation}deg)`,
            opacity: 0.8
          }}
        >
          ☕
        </div>
      ))}
      <style>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

const SnowTheme = () => {
  const [snowflakes, setSnowflakes] = useState([]);
  const [snowHeight, setSnowHeight] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);

  useEffect(() => {
    // Get stored snow height or start at 0
    const stored = localStorage.getItem('snowAccumulation');
    const initialHeight = stored ? parseFloat(stored) : 0;
    setSnowHeight(initialHeight);

    // Track document height
    const updateHeight = () => {
      setPageHeight(document.documentElement.scrollHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);

    // Create static falling snowflakes - each with unique fixed positions
    const createFlakes = () => {
      return Array.from({ length: 200 }, (_, i) => ({
        id: Date.now() + i,
        left: Math.random() * 100, // Random positions
        delay: Math.random() * 30, // More varied delays
        duration: 30 + Math.random() * 40, // 30-70 seconds (much longer)
        size: 2 + Math.random() * 5,
        opacity: 0.3 + Math.random() * 0.7,
        drift: (Math.random() - 0.5) * 40 // Horizontal drift
      }));
    };
    
    setSnowflakes(createFlakes());

    // When a snowflake completes its journey, add to snow height
    const snowfallDuration = 25000; // Average duration of fall
    const accumulation = setInterval(() => {
      setSnowHeight(prev => {
        if (prev >= 300) return prev; // Max height
        
        // Each interval represents snowflakes landing
        const increment = 0.3; // Small increment per snowflake landing
        const newHeight = prev + increment;
        
        localStorage.setItem('snowAccumulation', newHeight.toString());
        return newHeight;
      });
    }, 100); // Check frequently for smooth buildup

    // Refresh snowflakes periodically so they keep falling
    const refresh = setInterval(() => {
      setSnowflakes(createFlakes());
    }, 60000); // Every 60 seconds

    return () => {
      clearInterval(accumulation);
      clearInterval(refresh);
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <>
      {/* Falling snow particles — single keyframe, per-flake CSS vars */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${pageHeight}px`,
        pointerEvents: 'none',
        zIndex: 1,
        overflow: 'visible'
      }}>
        {snowflakes.map(flake => (
          <div
            key={flake.id}
            style={{
              position: 'absolute',
              top: '-50px',
              left: `${flake.left}%`,
              width: `${flake.size}px`,
              height: `${flake.size}px`,
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              opacity: flake.opacity,
              boxShadow: '0 0 8px rgba(255, 255, 255, 0.6)',
              '--snow-fall-y': `${pageHeight + 100}px`,
              '--snow-drift-x': `${flake.drift}px`,
              '--snow-opacity': flake.opacity,
              animation: `snowfall ${flake.duration}s linear ${flake.delay}s infinite`,
            }}
          />
        ))}
        <style>{`
          @keyframes snowfall {
            0% {
              transform: translateY(0) translateX(0);
              opacity: var(--snow-opacity, 0.6);
            }
            95% {
              opacity: var(--snow-opacity, 0.6);
            }
            100% {
              transform: translateY(var(--snow-fall-y, 100vh)) translateX(var(--snow-drift-x, 0));
              opacity: 0;
            }
          }
        `}</style>
      </div>

      {/* Accumulated snow layer at footer - smooth buildup */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
        height: `${snowHeight}px`,
        pointerEvents: 'none',
        zIndex: 9997,
        transition: 'height 0.1s linear',
        overflow: 'visible'
      }}>
        {/* Smooth snow surface with dynamic curves */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1200 300"
          preserveAspectRatio="none"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            filter: 'drop-shadow(0 -2px 8px rgba(200, 230, 255, 0.3))'
          }}
        >
          {/* Back layer */}
          <path
            d="M 0 300 L 0 200 Q 300 180 600 200 T 1200 200 L 1200 300 Z"
            fill="rgba(235, 245, 255, 0.6)"
            style={{ transform: `translateY(${300 - snowHeight * 0.5}px)`, transition: 'transform 0.1s linear' }}
          />
          {/* Middle layer */}
          <path
            d="M 0 300 L 0 230 Q 200 210 400 230 T 800 230 T 1200 230 L 1200 300 Z"
            fill="rgba(245, 250, 255, 0.8)"
            style={{ transform: `translateY(${300 - snowHeight * 0.7}px)`, transition: 'transform 0.1s linear' }}
          />
          {/* Front layer */}
          <path
            d="M 0 300 L 0 250 Q 150 235 300 250 T 600 250 T 900 250 T 1200 250 L 1200 300 Z"
            fill="rgba(255, 255, 255, 0.95)"
            style={{ transform: `translateY(${300 - snowHeight * 0.9}px)`, transition: 'transform 0.1s linear' }}
          />
        </svg>
        
        {/* Sparkles on snow surface */}
        {snowHeight > 10 && Array.from({ length: Math.min(Math.floor(snowHeight / 8), 50) }, (_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: `${5 + (i * 11) % (Math.max(snowHeight * 0.9, 10))}px`,
              left: `${(i * 13) % 100}%`,
              width: '2px',
              height: '2px',
              background: 'rgba(200, 230, 255, 0.9)',
              borderRadius: '50%',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.9)',
              animation: `sparkle ${1.5 + (i % 3) * 0.5}s ease-in-out ${(i % 4) * 0.5}s infinite`
            }}
          />
        ))}
        
        <style>{`
          @keyframes sparkle {
            0%, 100% {
              opacity: 0;
              transform: scale(0.5);
            }
            50% {
              opacity: 1;
              transform: scale(1.5);
            }
          }
        `}</style>
      </div>

      {/* Frost effect on edges and corners */}
      <style>{`
        /* Frosted corners and edges overlay */
        body::after {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 9999;
          background: 
            radial-gradient(ellipse at top left, rgba(200, 230, 255, 0.3) 0%, transparent 40%),
            radial-gradient(ellipse at top right, rgba(200, 230, 255, 0.3) 0%, transparent 40%),
            radial-gradient(ellipse at bottom left, rgba(200, 230, 255, 0.3) 0%, transparent 40%),
            radial-gradient(ellipse at bottom right, rgba(200, 230, 255, 0.3) 0%, transparent 40%);
          box-shadow: 
            inset 0 0 100px rgba(200, 230, 255, 0.2),
            inset 0 0 200px rgba(150, 200, 255, 0.1);
        }

        /* Subtle frost on glass cards */
        .glass-card {
          border: 1px solid rgba(200, 230, 255, 0.3) !important;
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(200, 230, 255, 0.2),
            inset 0 1px 0 rgba(200, 230, 255, 0.1),
            inset 0 0 30px rgba(150, 200, 255, 0.05) !important;
        }

        .glass-card::before {
          background: linear-gradient(135deg, 
            rgba(200, 230, 255, 0.2) 0%, 
            rgba(150, 200, 255, 0.15) 50%,
            rgba(200, 230, 255, 0.2) 100%) !important;
        }

        /* Frost edges on navigation */
        nav {
          box-shadow: 
            0 4px 20px rgba(0, 0, 0, 0.3),
            inset 0 -1px 0 rgba(200, 230, 255, 0.2) !important;
        }

        /* Frost edges on footer */
        footer {
          box-shadow: 
            0 -4px 20px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(200, 230, 255, 0.2) !important;
        }
      `}</style>
    </>
  );
};

export const KonamiEffects = ({ coffeeRainActive, snowThemeActive }) => {
  return (
    <>
      {coffeeRainActive && <CoffeeRain />}
      {snowThemeActive && <SnowTheme />}
    </>
  );
};

export default KonamiEffects;
